import React, { useMemo, useState, useEffect } from "react";
import { SectionEntity } from "@/database";
import { Folder, FolderOpen, FileText, Play } from "lucide-react";
import { Button } from "../..";

interface DenmarkSectionTreeProps {
  sections: SectionEntity[];
  selectedSectionIds?: Array<number | string>;
  onSectionClick: (section: SectionEntity) => void;
  getDenmarkChildSectionList: (params: any) => void;
  isSectionLoading: boolean;
  showPriorityPanel?: boolean;
  onPrioritySectionSelect?: (section: SectionEntity) => void;
  selectedHost?: string;
  priorityList?: any;
  disablePlayButton?: boolean;
}

const DenmarkSectionTree: React.FC<DenmarkSectionTreeProps> = ({
  sections,
  selectedSectionIds = [],
  onSectionClick,
  getDenmarkChildSectionList,
  isSectionLoading,
  showPriorityPanel,
  onPrioritySectionSelect,
  selectedHost,
  disablePlayButton = false,
}) => {
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});
  const [hoveredSection, setHoveredSection] = useState<string | null>(null);
  const [fetchedSectionIds, setFetchedSectionIds] = useState<Set<string>>(
    () => new Set()
  );
  /** The single section key currently waiting for RPC to resolve. */
  const [loadingSectionKey, setLoadingSectionKey] = useState<string | null>(
    null
  );

  // When Redux loading flips to false, clear the tracked loading key
  useEffect(() => {
    if (!isSectionLoading && loadingSectionKey !== null) {
      setLoadingSectionKey(null);
    }
  }, [isSectionLoading, loadingSectionKey]);

  const selectedSet = useMemo(
    () => new Set(selectedSectionIds.map((id) => String(id))),
    [selectedSectionIds]
  );

  const normalizedSections = useMemo(() => {
    if (!Array.isArray(sections) || sections.length === 0) return [];

    const hasNestedChildren = sections.some(
      (section) =>
        Array.isArray(section.childSections) && section.childSections.length > 0
    );
    if (hasNestedChildren) return sections;

    const sectionMap = new Map<string, SectionEntity>();
    const roots: SectionEntity[] = [];

    sections.forEach((section) => {
      const key =
        section.msId != null ? String(section.msId) : String(section["@id"]);
      sectionMap.set(key, { ...section, childSections: [] });
    });

    sectionMap.forEach((sectionNode) => {
      const parentId = sectionNode.parents?.[0]?.parentId;
      const nodeId = sectionNode.msId;
      const hasParent =
        parentId != null &&
        nodeId != null &&
        parentId !== nodeId &&
        sectionMap.has(String(parentId));

      if (hasParent) {
        sectionMap.get(String(parentId))?.childSections?.push(sectionNode);
      } else {
        roots.push(sectionNode);
      }
    });

    return roots;
  }, [sections]);

  const getSectionId = (section: SectionEntity): string => {
    if (section.msId != null) return String(section.msId);
    if (section["@id"]) {
      const matches = String(section["@id"]).match(/(\d+)$/);
      if (matches?.[1]) return matches[1];
      return String(section["@id"]);
    }
    if (section.path) return section.path;
    return section.name ?? "";
  };

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const renderSection = (section: SectionEntity, level: number) => {
    const sectionKey = getSectionId(section);
    const children = section.childSections ?? [];
    const canExpand = children.length > 0;
    const isExpanded = !!expandedSections[sectionKey];
    const isSelected = selectedSet.has(sectionKey);
    const isHovered = hoveredSection === sectionKey;
    const isTopParent = level === 0;
    const isLoadingThis = isSectionLoading && loadingSectionKey === sectionKey;

    return (
      <div key={`${sectionKey}-${level}`}>
        <div
          role="button"
          tabIndex={0}
          className="btn w-100 d-flex align-items-center text-start"
          style={{
            minHeight: "30px",
            padding: "5px 8px",
            marginBottom: "1px",
            paddingLeft: `${level * 22}px`,
            backgroundColor: isSelected
              ? "#dfe6eb"
              : isHovered
                ? "#f3f6f8"
                : "transparent",
            color: "#565b60",
            fontSize: "18px",
            fontWeight: 500,
            textTransform: "none",
            transition:
              "background-color 0.2s ease, transform 0.15s ease, box-shadow 0.15s ease",
            transform: isHovered ? "translateY(-2px)" : "translateY(0)",
            boxShadow: isHovered ? "0 4px 10px rgba(0,0,0,0.1)" : "none",
            borderRadius: isHovered ? "6px" : "0",
            zIndex: isHovered ? 1 : "auto",
            position: "relative",
          }}
          onMouseEnter={() => setHoveredSection(sectionKey)}
          onMouseLeave={() =>
            setHoveredSection((prev) => (prev === sectionKey ? null : prev))
          }
          onClick={() => {
            if (canExpand) {
              toggleSection(sectionKey);
            }
          }}
          onKeyDown={(event) => {
            if ((event.key === "Enter" || event.key === " ") && canExpand) {
              event.preventDefault();
              toggleSection(sectionKey);
            }
          }}
        >
          <span
            className="me-2 d-flex align-items-center justify-content-center"
            style={{ width: "28px", minWidth: "28px" }}
          >
            {isTopParent ? (
              isExpanded ? (
                <FolderOpen size={22} color="#555f6b" />
              ) : (
                <Folder size={22} color="#555f6b" />
              )
            ) : (
              <FileText size={18} color="#555f6b" />
            )}
          </span>
          <span
            className="text-truncate"
            onClick={(event) => {
              event.stopPropagation();
              const alreadyFetched = fetchedSectionIds.has(sectionKey);
              if (!alreadyFetched) {
                getDenmarkChildSectionList({
                  parentId: section.msId,
                  parentsParentId: section.parents?.[0]?.parentId,
                  hostId: Number(section.parents[0].hostId),
                  startIndex: 0,
                  recordsPerPage: 500,
                  listActivationStatus: [0, 1, 2, 3, 4, 5, 6, 7, 9],
                });
                setFetchedSectionIds((prev) => new Set(prev).add(sectionKey));
                setLoadingSectionKey(sectionKey);
              }
              toggleSection(sectionKey);
            }}
          >
            {section.name ?? "Untitled section"}
          </span>
          <span className="ms-auto d-flex align-items-center">
            {isLoadingThis ? (
              <span
                className="spinner-border spinner-border-sm text-primary"
                role="status"
                style={{ width: "18px", height: "18px" }}
              />
            ) : (
              <Button
                type="outline-primary"
                HTMLButtonType="button"
                icon={
                  <Play
                    size={11}
                    fill={disablePlayButton ? "#adb5bd" : "#1a87d4"}
                    color={disablePlayButton ? "#adb5bd" : "#1a87d4"}
                  />
                }
                className="btn-sm p-0 d-flex align-items-center justify-content-center no-fill-hover"
                style={{
                  width: "20px",
                  height: "20px",
                  minWidth: "20px",
                  opacity: isHovered ? 1 : 0,
                  visibility: isHovered ? "visible" : "hidden",
                  pointerEvents:
                    isHovered && !disablePlayButton ? "auto" : "none",
                  transition: "opacity 0.15s ease, visibility 0.15s ease",
                  cursor: disablePlayButton ? "not-allowed" : "pointer",
                  borderColor: disablePlayButton ? "#adb5bd" : undefined,
                }}
                isDisabled={disablePlayButton}
                onClick={(event) => {
                  event.stopPropagation();
                  if (disablePlayButton) return;
                  if (showPriorityPanel) {
                    onPrioritySectionSelect?.(section);
                  } else {
                    onSectionClick(section);
                  }
                }}
              />
            )}
          </span>
        </div>

        {canExpand && isExpanded && (
          <div>{children.map((child) => renderSection(child, level + 1))}</div>
        )}
      </div>
    );
  };

  if (!normalizedSections || normalizedSections.length === 0) {
    if (isSectionLoading && loadingSectionKey === null) {
      return (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ padding: "8px 8px" }}
        >
          <span
            className="spinner-border spinner-border-sm text-primary"
            role="status"
          />
        </div>
      );
    }
    return (
      <div
        className="d-flex align-items-center justify-content-center text-muted"
        style={{ minHeight: "500px", fontSize: "0.95rem" }}
      >
        No sections available
      </div>
    );
  }

  return (
    <div className="" style={{ maxHeight: "500px", overflowY: "auto" }}>
      {normalizedSections.map((section) => renderSection(section, 0))}
    </div>
  );
};

export default DenmarkSectionTree;
